
import React, { useCallback, useState } from 'react';
import { Button } from 'react-rainbow-components';

import { AxiosResponse } from 'axios';

import { Question, QuestionScoreChange } from '../../components';
import { sendMessage } from '../../services/chatbot';

export interface Item {
  questionGroup: string;
  questionTitle: string;
  questionValue: string;
}

interface Props {
  items: Item[];
  onResult: (messages: AxiosResponse[]) => void;
}

const Steps = ({ items, onResult }: Props): JSX.Element => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState(items);
  const [scores, setScores] = useState<QuestionScoreChange[]>([]);

  const handleBackClick = useCallback((): void => {
    if (currentStepIndex > 0) {
      const previousStepIndex = currentStepIndex - 1;
      setCurrentStepIndex(previousStepIndex);
    }
  }, [currentStepIndex]);

  const handleNextClick = useCallback((): void => {
    if (currentStepIndex < questions.length - 1) {
      const nextStepIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextStepIndex);
    }
  }, [currentStepIndex, questions]);

  const handleScoreChange = useCallback((score: QuestionScoreChange): void => {
    scores[currentStepIndex] = score;
    setScores(scores);
  }, [currentStepIndex, scores]);

  const handleSend = useCallback(async () => {
    // Soma a Pontuação do Perfis
    const scoreResult: { [score: string]: number } = {};
    scores.forEach((score) => {
      Object.keys(score).forEach((group) => {
        const previousScore = scoreResult[group];
        scoreResult[group] = previousScore ? previousScore + score[group] : score[group];
      });
    });
    // Pega o(s) Perfil(s) Com a Pontuação Mais Alta
    const personalities: string[] = [];
    Object.keys(scoreResult).forEach((group) => {
      if (scoreResult[group] === Math.max(...Object.values(scoreResult))) {
        personalities.push(group);
      }
    });

    setLoading(true);
    const promisses: Promise<AxiosResponse>[] = [];
    personalities.forEach((p) => promisses.push(sendMessage(p)));
    const messages = await Promise.all(promisses);
    onResult(messages);
    setLoading(false);
  }, [onResult, scores]);

  const handleValueChange = useCallback((value: string): void => {
    questions[currentStepIndex].questionValue = value;
    setQuestions([...questions]);
  }, [currentStepIndex, questions]);

  const isBackDisabled = useCallback((): boolean => {
    if (currentStepIndex > 0 && currentStepIndex < questions.length) {
      return false;
    }
    return true;
  }, [currentStepIndex, questions]);

  const isFinished = Boolean(questions[questions.length - 1].questionValue);
  const { questionGroup, questionTitle, questionValue } = questions[currentStepIndex];

  return (
    <>
      <p style={{ fontSize: 14, fontWeight: 'bold', textAlign: 'right' }}>
        {`${currentStepIndex + 1} de ${items.length}`}
      </p>
      <Question
        group={questionGroup}
        onScoreChange={handleScoreChange}
        onValueChange={handleValueChange}
        title={questionTitle}
        value={questionValue}
      />
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
        <Button
          disabled={isBackDisabled()}
          label="Voltar"
          onClick={handleBackClick}
          style={{ marginRight: 5 }}
          variant="neutral"
        />
        <Button
          disabled={!questionValue}
          isLoading={loading}
          label={isFinished ? 'Enviar' : 'Avançar'}
          onClick={isFinished ? handleSend : handleNextClick}
          variant="brand"
        />
      </div>
    </>
  );
}

export default Steps;
